import type { IncrementalResumo } from '../../lib/trpc';

export function SuccessState({
  nome,
  resultado,
  onNova,
  onConcluir,
}: {
  nome: string;
  resultado: IncrementalResumo;
  onNova: () => void;
  onConcluir: () => void;
}) {
  // "Atualizada" quando houve match com a carteira existente (re-importação);
  // "importada" quando foi tudo novo (primeira carga).
  const houveAtualizacao = resultado.atualizados + resultado.semMudanca + resultado.divergencias > 0;
  return (
    <div className="card px-8 py-12 text-center" data-testid="import-success">
      <div
        className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full"
        style={{ background: 'var(--accent-soft)' }}
      >
        <svg viewBox="0 0 24 24" width="26" height="26" fill="none">
          <path d="M20 6L9 17l-5-5" stroke="var(--accent-bright)" strokeWidth="2.4"
            strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
      <h2 className="text-lg font-semibold text-ink">
        {houveAtualizacao ? 'Carteira atualizada com sucesso' : 'Base importada com sucesso'}
      </h2>
      <p className="mx-auto mt-2 max-w-md text-sm text-ink-3">
        Aplicamos <span className="text-ink-2">{nome}</span> sobre a sua carteira, sem duplicar contratos.
      </p>

      <div className="mx-auto mt-6 grid max-w-xl grid-cols-2 gap-3 sm:grid-cols-4">
        <Card n={resultado.novos} l="novos" destaque />
        <Card n={resultado.atualizados} l="atualizados" destaque sub={subAtualizados(resultado)} />
        <Card n={resultado.semMudanca} l="sem mudança" />
        <Card n={resultado.divergencias} l="divergências" risco={resultado.divergencias > 0} />
      </div>

      {resultado.divergencias > 0 && (
        <p className="mx-auto mt-4 max-w-md text-xs text-ink-3">
          {resultado.divergencias} divergência{resultado.divergencias > 1 ? 's' : ''} (valor do contrato ou data-base)
          não {resultado.divergencias > 1 ? 'foram' : 'foi'} sobrescrita{resultado.divergencias > 1 ? 's' : ''} — revise quando puder.
        </p>
      )}
      {resultado.ausentes > 0 && (
        <p className="mx-auto mt-2 max-w-md text-xs text-ink-4">
          {resultado.ausentes} contrato{resultado.ausentes > 1 ? 's' : ''} da base {resultado.ausentes > 1 ? 'não vieram' : 'não veio'} nesta
          planilha e {resultado.ausentes > 1 ? 'foram mantidos' : 'foi mantido'} (nada é apagado).
        </p>
      )}

      <div className="mt-7 flex justify-center gap-3">
        <button className="btn btn-ghost" onClick={onNova}>
          Importar outra base
        </button>
        <button className="btn btn-primary" onClick={onConcluir}>
          Concluir
        </button>
      </div>
    </div>
  );
}

function subAtualizados(r: IncrementalResumo): string | undefined {
  const partes = [
    r.parcelasPagas > 0 ? `${r.parcelasPagas} parcela${r.parcelasPagas > 1 ? 's' : ''} paga${r.parcelasPagas > 1 ? 's' : ''}` : null,
    r.parcelasNovas > 0 ? `${r.parcelasNovas} nova${r.parcelasNovas > 1 ? 's' : ''}` : null,
    r.contatosPreenchidos > 0 ? `${r.contatosPreenchidos} contato${r.contatosPreenchidos > 1 ? 's' : ''}` : null,
  ].filter(Boolean);
  return partes.length ? partes.join(' · ') : undefined;
}

function Card({ n, l, destaque, risco, sub }: { n: number; l: string; destaque?: boolean; risco?: boolean; sub?: string }) {
  const cor = risco ? 'var(--risk)' : destaque && n > 0 ? 'var(--accent-bright)' : 'var(--ink)';
  return (
    <div className="card p-4">
      <div className="num text-2xl font-semibold" style={{ color: cor }}>
        {n}
      </div>
      <div className="mt-1 text-xs text-ink-3">{l}</div>
      {sub && <div className="mt-0.5 text-[11px] text-ink-4">{sub}</div>}
    </div>
  );
}
